import { useState, useMemo } from 'react';
import { Check, UserPlus, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { PersonAvatar } from '../shared';
import { useVolunteers, useUpdateUserCapabilities } from '@/hooks/queries';
import { useCreateVendor } from '@/hooks/queries/useVendors';
import { useCampaignStore } from '@/stores';
import { CanvasserType } from '@signalvote/shared';
import type { VendorResponse } from '@signalvote/shared';
import { readyStaff } from '@/lib/staffRead';

interface VendorDetailPanelProps {
  vendor: VendorResponse | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When true and vendor is null, shows the create vendor form first. */
  createMode?: boolean;
  /** Called after a vendor is successfully created, so parent can set selectedVendor. */
  onVendorCreated?: (vendor: VendorResponse) => void;
}

/**
 * Shell: picks the mode and owns the dialog frame. Each mode's state lives in
 * its own child, remounted per vendor and unmounted by Radix on close, so an
 * in-progress search or half-typed vendor name survives a background refetch of
 * the vendor list but never leaks across vendors.
 */
export function VendorDetailPanel({
  vendor,
  open,
  onOpenChange,
  createMode = false,
  onVendorCreated,
}: VendorDetailPanelProps) {
  if (!vendor && createMode) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[480px]">
          <CreateVendorForm onOpenChange={onOpenChange} onVendorCreated={onVendorCreated} />
        </DialogContent>
      </Dialog>
    );
  }

  if (!vendor) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <VendorMembers key={vendor.id} vendor={vendor} />
      </DialogContent>
    </Dialog>
  );
}

/** Name-a-new-vendor form. Owns only the name being typed. */
function CreateVendorForm({
  onOpenChange,
  onVendorCreated,
}: {
  onOpenChange: (open: boolean) => void;
  onVendorCreated?: (vendor: VendorResponse) => void;
}) {
  const { mutate: createVendor, isPending: creating } = useCreateVendor();
  const [newVendorName, setNewVendorName] = useState('');

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newVendorName.trim()) return;

    createVendor(
      { name: newVendorName.trim() },
      {
        onSuccess: (created) => {
          onVendorCreated?.(created);
        },
      },
    );
  };

  return (
    <form onSubmit={handleCreateSubmit}>
      <DialogHeader>
        <DialogTitle>Create Vendor</DialogTitle>
        <DialogDescription>
          Add a vendor company that supplies paid canvassers.
        </DialogDescription>
      </DialogHeader>

      <div className="py-4">
        <div className="space-y-2">
          <label htmlFor="vendor-name" className="text-sm font-medium">
            Vendor Name <span className="text-destructive">*</span>
          </label>
          <Input
            id="vendor-name"
            placeholder="e.g., FieldWorks LLC"
            value={newVendorName}
            onChange={(e) => setNewVendorName(e.target.value)}
            autoFocus
          />
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!newVendorName.trim() || creating}
        >
          {creating ? 'Creating...' : 'Create Vendor'}
        </Button>
      </DialogFooter>
    </form>
  );
}

/** Vendor detail: who is assigned to this vendor, plus add / remove. */
function VendorMembers({ vendor }: { vendor: VendorResponse }) {
  const allVolunteers = readyStaff(useVolunteers(undefined));
  const { mutate: updateCapabilities, isPending } = useUpdateUserCapabilities();
  const campaignId = useCampaignStore((s) => s.selectedCampaign?.id);
  const [showAddMember, setShowAddMember] = useState(false);
  const [search, setSearch] = useState('');
  const [recentlyAdded, setRecentlyAdded] = useState<Set<string>>(new Set());

  const assignedCanvassers = useMemo(
    () => allVolunteers.filter((v) => v.vendorId === vendor.id),
    [allVolunteers, vendor.id],
  );

  const availableCanvassers = useMemo(
    () => allVolunteers.filter((v) => !v.vendorId),
    [allVolunteers],
  );

  const filteredAvailable = useMemo(() => {
    if (!search.trim()) return availableCanvassers.slice(0, 10);
    const q = search.toLowerCase();
    return availableCanvassers
      .filter(
        (v) =>
          v.firstName.toLowerCase().includes(q) ||
          v.lastName.toLowerCase().includes(q) ||
          v.email.toLowerCase().includes(q),
      )
      .slice(0, 10);
  }, [availableCanvassers, search]);

  const handleAddToVendor = (userId: string) => {
    if (!campaignId) return;
    updateCapabilities({
      campaignId,
      userId,
      data: { canvasserType: CanvasserType.Paid, vendorId: vendor.id },
    });
    setRecentlyAdded((prev) => new Set(prev).add(userId));
  };

  const handleRemoveFromVendor = (userId: string) => {
    if (!campaignId) return;
    updateCapabilities({
      campaignId,
      userId,
      data: { clearCanvasserType: true, vendorId: null },
    });
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{vendor.name}</DialogTitle>
        <DialogDescription>
          {assignedCanvassers.length}{' '}
          {assignedCanvassers.length === 1 ? 'canvasser' : 'canvassers'} assigned
        </DialogDescription>
      </DialogHeader>

      <Separator />

      {/* Members section */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-sm font-medium">
            Canvassers ({assignedCanvassers.length})
          </h4>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setShowAddMember(!showAddMember)}
          >
            <UserPlus className="mr-1 h-3 w-3" />
            Add
          </Button>
        </div>

        {/* Add canvasser search */}
        {showAddMember && (
          <div className="mb-3 space-y-2">
            <p className="text-xs text-muted-foreground">
              Click a person to add them. They will be set to Paid under this vendor.
            </p>
            <Input
              placeholder="Search people to add..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-sm"
              autoFocus
            />
            {filteredAvailable.length > 0 ? (
              <div className="max-h-[200px] overflow-y-auto rounded-md border">
                {filteredAvailable.map((v) => {
                  const justAdded = recentlyAdded.has(v.userId);
                  return (
                    <button
                      key={v.userId}
                      className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent disabled:opacity-50 ${justAdded ? 'bg-green-50' : ''}`}
                      onClick={() => handleAddToVendor(v.userId)}
                      disabled={isPending || justAdded}
                    >
                      <PersonAvatar
                        firstName={v.firstName}
                        lastName={v.lastName}
                        size="sm"
                      />
                      <div className="min-w-0 flex-1">
                        <span className="block truncate text-sm">
                          {v.firstName} {v.lastName}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {v.email}
                        </span>
                      </div>
                      {justAdded && (
                        <span className="flex items-center gap-1 text-xs text-green-600">
                          <Check className="h-3 w-3" />
                          Added!
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="py-2 text-center text-xs text-muted-foreground">
                {search ? 'No matching people found' : 'No available people to add'}
              </p>
            )}
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setShowAddMember(false)}
              >
                Done
              </Button>
            </div>
          </div>
        )}

        {/* Current members */}
        {assignedCanvassers.length === 0 ? (
          <p className="py-4 text-center text-xs text-muted-foreground">
            No canvassers assigned to this vendor yet.
          </p>
        ) : (
          <div className="space-y-1">
            {assignedCanvassers.map((v) => (
              <div
                key={v.userId}
                className="group flex items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-accent"
              >
                <PersonAvatar
                  firstName={v.firstName}
                  lastName={v.lastName}
                  size="sm"
                />
                <div className="min-w-0 flex-1">
                  <span className="block truncate text-sm">
                    {v.firstName} {v.lastName}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {v.email}
                  </span>
                </div>
                <button
                  title={`Remove ${v.firstName} from vendor`}
                  className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-muted-foreground opacity-0 transition-all group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => handleRemoveFromVendor(v.userId)}
                  disabled={isPending}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
